import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getTodayStart } from '../utils/formatters';
import { encryptData, decryptData, isEncrypted } from '../utils/encryption';

export function useTimeEntries(user, isOnline) {
  const [entries, setEntries] = useState([]);
  const [syncQueue, setSyncQueue] = useState([]);

  // Load local data when user logs in (with encryption support)
  useEffect(() => {
    if (user) {
      (async () => {
        try {
          const savedEntries = localStorage.getItem(`entries_${user.uid}`);
          if (savedEntries) {
            if (isEncrypted(savedEntries)) {
              const decrypted = await decryptData(savedEntries, user.uid);
              if (decrypted) setEntries(decrypted);
            } else {
              // Backward compatibility: unencrypted data
              setEntries(JSON.parse(savedEntries));
            }
          }

          const savedQueue = localStorage.getItem(`syncQueue_${user.uid}`);
          if (savedQueue) {
            if (isEncrypted(savedQueue)) {
              const decrypted = await decryptData(savedQueue, user.uid);
              if (decrypted) setSyncQueue(decrypted);
            } else {
              // Backward compatibility: unencrypted data
              setSyncQueue(JSON.parse(savedQueue));
            }
          }
        } catch (error) {
          console.error('Error loading encrypted data:', error);
        }
      })();
    } else {
      setEntries([]);
      setSyncQueue([]);
    }
  }, [user]);

  // Save entries to localStorage (with encryption)
  useEffect(() => {
    if (user && entries.length > 0) {
      (async () => {
        try {
          const encrypted = await encryptData(entries, user.uid);
          localStorage.setItem(`entries_${user.uid}`, encrypted);
        } catch (error) {
          console.error('Error saving encrypted entries:', error);
        }
      })();
    }
  }, [entries, user]);

  // Save sync queue to localStorage (with encryption)
  useEffect(() => {
    if (user && syncQueue.length > 0) {
      (async () => {
        try {
          const encrypted = await encryptData(syncQueue, user.uid);
          localStorage.setItem(`syncQueue_${user.uid}`, encrypted);
        } catch (error) {
          console.error('Error saving encrypted sync queue:', error);
        }
      })();
    }
  }, [syncQueue, user]);

  // Sync with Firestore
  useEffect(() => {
    if (!user || !isOnline) return;

    const q = query(
      collection(db, 'timeEntries'),
      where('userId', '==', user.uid),
      orderBy('startTime', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const firestoreEntries = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setEntries(prevEntries => {
        const merged = [...firestoreEntries];
        prevEntries.forEach(localEntry => {
          if (!merged.find(e => e.id === localEntry.id)) {
            merged.push(localEntry);
          }
        });
        return merged.sort((a, b) => b.startTime - a.startTime);
      });
    }, (error) => {
      console.error('Firestore sync error:', error);
    });

    return unsubscribe;
  }, [user, isOnline]);

  const addToSyncQueue = useCallback((operation) => {
    setSyncQueue(prev => {
      const newQueue = [...prev, operation];
      if (user) {
        (async () => {
          try {
            const encrypted = await encryptData(newQueue, user.uid);
            localStorage.setItem(`syncQueue_${user.uid}`, encrypted);
          } catch (error) {
            console.error('Error saving encrypted sync queue:', error);
          }
        })();
      }
      return newQueue;
    });
  }, [user]);

  const processSyncQueue = useCallback(async () => {
    if (!user || syncQueue.length === 0) return;

    const queue = [...syncQueue];
    setSyncQueue([]);
    if (user) {
      localStorage.removeItem(`syncQueue_${user.uid}`);
    }

    for (const operation of queue) {
      try {
        if (operation.type === 'create') {
          await setDoc(doc(db, 'timeEntries', operation.data.id.toString()), {
            ...operation.data,
            userId: user.uid,
            syncedAt: Date.now()
          });
        } else if (operation.type === 'update') {
          await updateDoc(doc(db, 'timeEntries', operation.data.id.toString()), {
            ...operation.data,
            syncedAt: Date.now()
          });
        } else if (operation.type === 'delete') {
          await deleteDoc(doc(db, 'timeEntries', operation.id.toString()));
        }
      } catch (error) {
        console.error('Sync operation failed:', error);
        addToSyncQueue(operation);
      }
    }
  }, [user, syncQueue, addToSyncQueue]);

  // Process sync queue when coming online
  useEffect(() => {
    if (isOnline && user && syncQueue.length > 0) {
      processSyncQueue();
    }
  }, [isOnline, user, syncQueue.length, processSyncQueue]);

  const saveEntry = useCallback(async (entry) => {
    if (!user) return;

    if (isOnline) {
      try {
        await setDoc(doc(db, 'timeEntries', entry.id.toString()), {
          ...entry,
          userId: user.uid,
          syncedAt: Date.now()
        });
      } catch (error) {
        console.error('Save error:', error);
        addToSyncQueue({ type: 'create', data: entry });
      }
    } else {
      addToSyncQueue({ type: 'create', data: entry });
    }
  }, [user, isOnline, addToSyncQueue]);

  const addEntry = useCallback(async (entry) => {
    setEntries(prev => [entry, ...prev].sort((a, b) => b.startTime - a.startTime));
    await saveEntry(entry);
  }, [saveEntry]);

  const updateEntry = useCallback(async (entryId, updates) => {
    const updatedEntry = { id: entryId, ...updates };

    setEntries(prev => prev.map(e => e.id === entryId ? { ...e, ...updates } : e));

    if (user) {
      if (isOnline) {
        try {
          await updateDoc(doc(db, 'timeEntries', entryId.toString()), {
            ...updates,
            syncedAt: Date.now()
          });
        } catch (error) {
          console.error('Update error:', error);
          addToSyncQueue({ type: 'update', data: updatedEntry });
        }
      } else {
        addToSyncQueue({ type: 'update', data: updatedEntry });
      }
    }
  }, [user, isOnline, addToSyncQueue]);

  const deleteEntry = useCallback(async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    setEntries(prev => prev.filter(e => e.id !== entryId));

    if (user) {
      if (isOnline) {
        try {
          await deleteDoc(doc(db, 'timeEntries', entryId.toString()));
        } catch (error) {
          console.error('Delete error:', error);
          addToSyncQueue({ type: 'delete', id: entryId });
        }
      } else {
        addToSyncQueue({ type: 'delete', id: entryId });
      }
    }
  }, [user, isOnline, addToSyncQueue]);

  const getTodayEntries = useCallback(() => {
    const todayTimestamp = getTodayStart();
    return entries.filter(entry => entry.startTime >= todayTimestamp);
  }, [entries]);

  const clearUserData = useCallback(() => {
    setEntries([]);
    setSyncQueue([]);
  }, []);

  return {
    entries,
    syncQueue,
    addEntry,
    updateEntry,
    deleteEntry,
    getTodayEntries,
    clearUserData
  };
}
